import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Message } from "@arco-design/web-react";
import "./Layout.scss";

import { getAppLatest, logout } from "@/api/api";
import { ROUTES } from "@/constants";
import { openLoginDialog, openPointsDetailDialog } from "@/dialog";
import { useUserStore } from "@/store/useUserStore";
import { getToken, removeToken } from "@/utils/auth";

interface LayoutProps {
  children?: ReactNode;
}

/** 左侧栏菜单项（文案 key 见 locales） */
interface LayoutSideNavItem {
  id: string;
  labelKey: string;
  iconSrc: string;
  to?: string;
  /** 与 `to` 拼接为 `Link`，如 `#exploreAll` */
  href?: string;
  /** 与同 `to` 的其它带 hash 项共存时，当前 hash 未命中任一锚点则高亮此项（如 `/home` 无 hash 或页内其它锚点） */
  activeWhenHashUnmatched?: boolean;
}

// Figma node 1874:418 导出资源（侧栏图标）
const LAYOUT_SIDE_NAV_ITEMS: readonly LayoutSideNavItem[] = [
  {
    id: "home",
    labelKey: "layout.nav.home",
    iconSrc: new URL('@/assets/images/menu_home.png', import.meta.url).href,
    to: ROUTES.HOME,
    href: "#homeSwiper",
    activeWhenHashUnmatched: true,
  },
  {
    id: "skillhub",
    labelKey: "layout.nav.skillhub",
    iconSrc: new URL('@/assets/images/menu_skillhub.png', import.meta.url).href,
    to: ROUTES.HOME,
    href: "#exploreAll",
  },
  {
    id: "api",
    labelKey: "layout.nav.api",
    iconSrc: new URL('@/assets/images/menu_api.png', import.meta.url).href,
    to: ROUTES.PERSONAL_TOKEN,
  },
  {
    id: "billing",
    labelKey: "layout.nav.billing",
    iconSrc: new URL('@/assets/images/menu_billing.png', import.meta.url).href,
    to: ROUTES.SUBSCRIBE
  },
];

// Figma 参考图中用到的资源（node 1874:418 导出）
// const img27 = "https://www.figma.com/api/mcp/asset/30c56532-65a3-4d95-b3a3-cb0fdcfe89a5";
const imgMac = new URL('@/assets/images/ic_mac.png', import.meta.url).href;
const imgWindows = new URL('@/assets/images/ic_windows.png', import.meta.url).href;
// const imgUserArrow = "https://www.figma.com/api/mcp/asset/e8a2a089-9fa9-4c57-9e7a-a395f17f0148";

/**
 * 主布局（与路由结合）：顶部栏 + 左侧栏 + 右侧内容区
 * Figma：Popi-（new--Web端-客户端端）node 1874:418
 *
 * 说明：content 区域由各路由页面自己渲染，这里只保证外层布局像素级对齐。
 */
function isSideNavItemActive(
  item: LayoutSideNavItem,
  pathname: string,
  hash: string,
  items: readonly LayoutSideNavItem[],
): boolean {
  if (item.to == null) return false;

  const pathMatches =
    pathname === item.to ||
    (item.to !== "/" && pathname.startsWith(`${item.to}/`));
  if (!pathMatches) return false;

  const anchor = item.href?.startsWith("#") ? item.href : null;
  if (!anchor) return true;

  if (hash === anchor) return true;

  const samePathAnchors = items.filter(
    (i) => i.to === item.to && i.href?.startsWith("#"),
  );
  if (samePathAnchors.length <= 1) return false;

  const hashMatchesSibling = samePathAnchors.some((i) => i.href === hash);
  if (hashMatchesSibling) return false;

  return item.activeWhenHashUnmatched === true;
}

export function Layout({ children }: LayoutProps) {
  const { pathname, hash } = useLocation();
  const { t } = useTranslation();
  const { user, setUser, clearUser } = useUserStore();
  const isLogin = user != null;
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [macArmUrl, setMacArmUrl] = useState("");
  const [macIntelUrl, setMacIntelUrl] = useState("");
  const [windowsX64Url, setWindowsX64Url] = useState("");
  const avatarMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await getAppLatest();
        const data = res.data;
        if (!data) return;
        setMacArmUrl(data.macArm?.url ?? "");
        setMacIntelUrl(data.macIntel?.url ?? "");
        setWindowsX64Url(data.windowsX64?.url ?? "");
      } catch {
        // 下载链接保持默认空，按钮仍展示
      }
    })();
  }, []);

  useEffect(() => {
    if (!getToken()) {
      clearUser();

      return;
    }

    void setUser();
  }, [clearUser, setUser]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const onDocumentMouseDown = (evt: MouseEvent) => {
      const target = evt.target as Node | null;
      if (!avatarMenuRef.current || !target) return;
      if (!avatarMenuRef.current.contains(target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocumentMouseDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentMouseDown);
    };
  }, [userMenuOpen]);

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
    } catch {
      // 忽略接口错误，后续清理照常执行
    } finally {
      removeToken();
      clearUser();
      setUserMenuOpen(false);
      window.location.href = ROUTES.HOME;
    }
  };

  const goToPopi = () => {
    console.log(import.meta.env.VITE_POPI_URL);
    window.open(import.meta.env.VITE_POPI_URL, '_blank');
  };

  const handleDownloadClick = (
    url: string,
    evt: ReactMouseEvent<HTMLAnchorElement>,
  ): void => {
    if (url) return;
    evt.preventDefault();
    Message.info("暂无下载地址");
  };

  return (
    <div className="layoutShell">
      <header className="layoutShell__topbar" aria-label="顶部栏">
        <img className="layoutShell__logo" onClick={() => goToPopi()} src={new URL('@/assets/images/popiart.png', import.meta.url).href} alt="" />

        <div className="layoutShell__topbarRight" aria-label="用户区域">
          {/* <div className="layoutShell__functions">
            <img className="layoutShell__functionsIcon" src={imgRectangle34625901} alt="" />
          </div> */}
          <div className="layoutShell__userPill" aria-label="Popi.art" onClick={() => goToPopi()}>
            <img className="layoutShell__userPillDot" src={new URL('@/assets/images/dot.png', import.meta.url).href} alt="" />
            <span className="layoutShell__userPillText">{t("layout.brand")}</span>
          </div>
          {/* 用户积分 */}
          <div className="layoutShell__userPoints" onClick={() => openPointsDetailDialog()}>
            <img className="layoutShell__userPointsIcon" src={new URL('@/assets/images/ic_stars_active.png', import.meta.url).href} alt="" />
            <div className="layoutShell__userPointsValue">
              {String(user?.allCoins ?? 0)}
            </div>
            <div className="layoutShell__userPointsLabel">
              {t("layout.nav.points")}
            </div>
          </div>
          {
            isLogin ? (
              <div className="layoutShell__avatarMenu" ref={avatarMenuRef}>
                <button
                  type="button"
                  className="layoutShell__avatar"
                  onClick={() => setUserMenuOpen((v) => !v)}
                  aria-label="用户菜单"
                >
                  <img
                    className="layoutShell__avatarBg"
                    src={String(user?.avatar)}
                    alt=""
                  />
                </button>
                {userMenuOpen ? (
                  <div className="layoutShell__avatarDropdown" role="menu" aria-label="用户操作">
                    <button
                      type="button"
                      className="layoutShell__avatarDropdownItem"
                      role="menuitem"
                      onClick={() => void handleLogout()}
                    >
                      退出登录
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <button
                type="button"
                className="layoutShell__loginBtn"
                aria-label="登录/注册"
                onClick={() =>
                  void openLoginDialog({
                    onSelect: (action) => {
                      console.log("selected action:", action);
                    },
                  })
                }
              >
                登录/注册
              </button>
            )
          }
        </div>
      </header>

      <div className="layoutShell__body">
        <aside className="layoutShell__sidebar" aria-label="左侧栏">
          <nav className="layoutShell__sideNav" aria-label="侧边栏菜单">
            {LAYOUT_SIDE_NAV_ITEMS.map((item) => {
              const isActive = isSideNavItemActive(
                item,
                pathname,
                hash,
                LAYOUT_SIDE_NAV_ITEMS,
              );
              const className = [
                "layoutShell__sideItem",
                isActive ? "layoutShell__sideItem--active" : "",
              ]
                .filter(Boolean)
                .join(" ");
              const inner = (
                <>
                  <img className="layoutShell__sideIcon" src={item.iconSrc} alt="" />
                  <span className="layoutShell__sideText">{t(item.labelKey)}</span>
                </>
              );
              if (item.to) {
                const linkTo = item.href ? `${item.to}${item.href}` : item.to;
                return (
                  <Link key={item.id} to={linkTo} className={className}>
                    {inner}
                  </Link>
                );
              }
              return (
                <a key={item.id} href={item.href ?? "#"} className={className}>
                  {inner}
                </a>
              );
            })}
          </nav>
          <div className="layoutShell__footer">
            {/* <p className="layoutShell__downloadsTitle">应用下载</p>
            <a
              href={macArmUrl || "#"}
              className="layoutShell__downloadBtn layoutShell__downloadBtn--store"
              target={macArmUrl ? "_blank" : undefined}
              rel={macArmUrl ? "noopener noreferrer" : undefined}
              onClick={(e) => handleDownloadClick(macArmUrl, e)}
            >
              <img className="layoutShell__downloadBtnIcon" src={imgMac} alt="" />
              <span className="layoutShell__downloadBtnText">Mac M系列 下载</span>
            </a>
            <a
              href={macIntelUrl || "#"}
              className="layoutShell__downloadBtn layoutShell__downloadBtn--store"
              target={macIntelUrl ? "_blank" : undefined}
              rel={macIntelUrl ? "noopener noreferrer" : undefined}
              onClick={(e) => handleDownloadClick(macIntelUrl, e)}
            >
              <img className="layoutShell__downloadBtnIcon" src={imgMac} alt="" />
              <span className="layoutShell__downloadBtnText">Mac Intel 下载</span>
            </a>
            <a
              href={windowsX64Url || "#"}
              className="layoutShell__downloadBtn layoutShell__downloadBtn--store"
              target={windowsX64Url ? "_blank" : undefined}
              rel={windowsX64Url ? "noopener noreferrer" : undefined}
              onClick={(e) => handleDownloadClick(windowsX64Url, e)}
            >
              <img className="layoutShell__downloadBtnIcon" src={imgWindows} alt="" />
              <span className="layoutShell__downloadBtnText">Windows 下载</span>
            </a> */}
            <div className="layoutShell__downloadsDivider" />
            <div className="layoutShell__userCard" aria-label="用户信息">
              <img className="layoutShell__userAvatar" src={String(user?.avatar)} alt="" />
              <div className="layoutShell__userTexts">
                <span className="layoutShell__userName">
                  {String(user?.name)}
                </span>
                {/* <span className="layoutShell__userPlan">套餐状态</span> */}
              </div>
              {/* <img className="layoutShell__userRoleIcon" src={imgUserArrow} alt="" /> */}
            </div>
          </div>
        </aside>

        <main className="layoutShell__contentFrame" aria-label="内容区">
          {children}
        </main>
      </div>
    </div>
  );
}
